import m, { FactoryComponent } from 'mithril';
import { meiosisSetup } from 'meiosis-setup';
import { i18n, routingSvc, t } from '.';
import {
  ContextualItem,
  Dashboards,
  DataModel,
  ID,
  Narrative,
  Scenario,
  emptyModel,
  thresholdColors,
} from '../models';
import { loadStarterKit, migratePersonaImages, withRandomFirstBox } from '../models/starter-kit';
import { migrateUsageLegends } from '../models/usage-legend';
import type { Languages } from './translations';
import { ldb } from '../utils/local-ldb';
import {
  MeiosisCell,
  Service,
  Update,
  MeiosisComponent as MComp,
} from 'meiosis-setup/types';
import {
  LANGUAGE,
  SAVED,
  migrateModel,
  syncNarrativeDecisions,
  scrollToTop,
  validateNarrative,
} from '../utils';
import { uniqueId } from 'mithril-materialized';
import { normalizeInconsistencies } from 'morphological-box';

const MODEL_KEY = 'SG_MODEL';
const FONT_KEY = 'SPARK_FONT_SIZE';
const MAP_HEIGHT_KEY = 'SG_MAP_HEIGHT';
const BOX_TRANSPOSED_KEY = 'SG_BOX_TRANSPOSED';
const DEFAULT_FONT_SIZE = 16;
const DEFAULT_MAP_HEIGHT = 400;

export type State = {
  fontSize: number;
  page: Dashboards;
  model: DataModel;
  modelReady: boolean;
  modelLoadError: boolean;
  title: string;
  language: string;
  activeTooltip?: string;
  /** Current narrative that we are working on */
  curNarrative?: Narrative;
  /** Components that have been excluded from the narrative */
  excludedComps?: Record<ID, boolean>;
  /** Components that have been given a fixed value in the narrative */
  lockedComps?: Record<ID, boolean>;
  mapHeight: number;
  /** Show the morphological box with key drivers as rows and values horizontally */
  boxTransposed: boolean;
};

export type MeiosisComponent<T = {}> = FactoryComponent<MeiosisCell<State> & T>;

const setTitle = (title: string) => {
  document.title = `ScenarioSpark: ${title}`;
};

/* Actions */

export const setPage = (cell: MeiosisCell<State>, page: Dashboards): void => {
  scrollToTop();
  cell.update({ page });
};

export const changePage = (
  cell: MeiosisCell<State>,
  page: Dashboards,
  params?: Record<string, string | number | undefined>,
  query?: Record<string, string | number | undefined>
) => {
  routingSvc && routingSvc.switchTo(page, params, query);
  cell.update({ page });
};

export const setFontSize = (cell: MeiosisCell<State>, size: number) => {
  const fontSize = Math.max(12, Math.min(28, size)); // clamp between 12–28px
  document.documentElement.style.setProperty(
    '--morph-font-size',
    `${fontSize}px`
  );
  localStorage.setItem(FONT_KEY, String(fontSize));
  cell.update({ fontSize });
};

export const setBoxTransposed = (
  cell: MeiosisCell<State>,
  boxTransposed: boolean
) => {
  localStorage.setItem(BOX_TRANSPOSED_KEY, String(boxTransposed));
  cell.update({ boxTransposed });
};

const validateScenario = (scenario?: Scenario) => {
  if (!scenario) return false;
  // Repairs one-directional entries, e.g. from older starter kits
  scenario.inconsistencies = normalizeInconsistencies(
    scenario.inconsistencies || {}
  );
  if (!scenario.categories) scenario.categories = [];
  if (!scenario.components) scenario.components = [];
  if (!scenario.narratives) scenario.narratives = [];
  scenario.narratives = scenario.narratives.map((n) =>
    validateNarrative(n, scenario.components)
  );
  if (typeof scenario.hideInconsistentValues === 'undefined') {
    scenario.hideInconsistentValues = true;
  }
  if (!scenario.thresholdColors) scenario.thresholdColors = thresholdColors;
  scenario.narratives.forEach((c) => {
    if (!c.components) c.components = {};
    if (!c.id) c.id = uniqueId();
    if (!c.label) c.label = 'UNKNOWN';
  });
  scenario.categories.forEach((c) => {
    if (!c.componentIds) c.componentIds = [];
    if (!c.id) c.id = uniqueId();
    if (!c.label) c.label = 'UNKNOWN';
  });
  scenario.components.forEach((c) => {
    if (!c.values) c.values = [];
    if (!c.id) c.id = uniqueId();
    if (!c.label) c.label = 'UNKNOWN';
  });
  return true;
};

export const saveModel = async (
  cell: MeiosisCell<State>,
  model: DataModel,
  reset = false
) => {
  model.lastUpdate = Date.now();
  if (!model.scenarios) {
    model.scenarios = [];
  }
  // Imported or older scenarios store decisions in narratives: move them to courses of action
  migrateModel(model);
  migrateUsageLegends(model);
  // console.log(JSON.stringify(model, null, 2));
  if (reset) {
    if (!validateScenario(model.scenario)) {
      alert(t('JSON_NOT_VALID'));
      return;
    }
    await ldb.set(MODEL_KEY, JSON.stringify(model));
    cell.update({
      model: () => model,
      activeTooltip: '',
      title: model.scenario.label,
      curNarrative: () => undefined,
      excludedComps: () => ({}),
      lockedComps: () => ({}),
    });
  } else {
    await ldb.set(MODEL_KEY, JSON.stringify(model));
    cell.update({ model: () => model });
  }
  localStorage.setItem(SAVED, 'false');
};

export const selectScenarioFromCollection = async (
  cell: MeiosisCell<State>,
  selectedScenarioId: ID
) => {
  const { model } = cell.getState();
  const { scenario: oldScenario, scenarios = [] } = model;
  const newScenario = scenarios.find((s) => s.id === selectedScenarioId);
  if (oldScenario && newScenario) {
    model.scenarios = [
      oldScenario,
      ...scenarios.filter((s) => s.id !== selectedScenarioId),
    ];
    model.scenario = newScenario;
    cell.update({
      model: () => model,
      activeTooltip: '',
      title: newScenario.label,
      curNarrative: () => undefined,
      excludedComps: () => ({}),
      lockedComps: () => ({}),
    });
    await ldb.set(MODEL_KEY, JSON.stringify(model));
  }
};

export const saveNarrative = async (
  cell: MeiosisCell<State>,
  curNarrative: Narrative
) => {
  const { model } = cell.getState();
  if (!curNarrative.id) curNarrative.id = uniqueId();
  // A copy made before the decision categories changed must not undo their migration
  syncNarrativeDecisions(model.scenario, curNarrative);
  if (!model.scenario.narratives) {
    curNarrative.saved = true;
    model.scenario.narratives = [curNarrative];
  } else {
    if (curNarrative.saved) {
      model.scenario.narratives = model.scenario.narratives.map((n) =>
        n.id !== curNarrative.id ? n : curNarrative
      );
    } else {
      curNarrative.saved = true;
      model.scenario.narratives.push(curNarrative);
    }
  }
  cell.update({ curNarrative: () => curNarrative });
  saveModel(cell, model);
};

export const updateNarrative = async (
  cell: MeiosisCell<State>,
  curNarrative: Narrative
) => {
  if (curNarrative.saved) {
    await saveNarrative(cell, curNarrative);
  }
  cell.update({ curNarrative });
};

export const mutateScenarioComponent = (
  cell: MeiosisCell<State>,
  scenarioComponentId: ID,
  item: ContextualItem,
  mutation: 'update' | 'create' | 'delete'
) => {
  const { model } = cell.state;
  const {
    scenario: { components },
  } = model;
  const comp = components.filter((c) => c.id === scenarioComponentId).shift();
  if (!comp) {
    console.error('Scenario component not found!');
    return;
  }
  const { values = [] } = comp;
  comp.values =
    mutation === 'update'
      ? values.map((c) => (c.id === item.id ? item : c))
      : mutation === 'delete'
      ? values.filter((c) => c.id !== item.id)
      : [...values, item];
  if (mutation === 'delete') {
    model.scenario.narratives = model.scenario.narratives.map((n) =>
      validateNarrative(n, model.scenario.components)
    );
  }
  saveModel(cell, model);
};

/** Move the position of a scenario component before or after another scenario component */
export const moveScenarioComponent = (
  cell: MeiosisCell<State>,
  componentId: ID,
  moveId: ID,
  dropId: ID,
  moveBefore: boolean
) => {
  const { model } = cell.state;
  const {
    scenario: { components },
  } = model;
  const comp = components.filter((c) => c.id === componentId).shift();
  if (!comp) {
    console.error('Scenario component not found!');
    return;
  }
  const { values = [] } = comp;
  const itemToMove = values.find((item) => item.id === moveId);
  if (!itemToMove) return;
  const itemDropped = values.find((item) => item.id === dropId);
  if (!itemDropped) return;

  comp.values = values
    .filter((i) => i.id !== moveId)
    .reduce((acc, cur) => {
      if (cur.id === dropId) {
        if (moveBefore) {
          acc.push(itemToMove);
          acc.push(cur);
        } else {
          acc.push(cur);
          acc.push(itemToMove);
        }
      } else {
        acc.push(cur);
      }
      return acc;
    }, [] as ContextualItem[]);
  saveModel(cell, model);
};

export const setLanguage = async (
  cell: MeiosisCell<State>,
  locale = i18n.currentLocale
) => {
  localStorage.setItem(LANGUAGE, locale);
  await i18n.loadAndSetLocale(locale);
  cell.update({ language: locale });
};

export const setMapHeight = (cell: MeiosisCell<State>, height: number) => {
  localStorage.setItem(MAP_HEIGHT_KEY, String(height));
  cell.update({ mapHeight: height });
};
/* END OF Actions */

const initialize = async (update: Update<State>) => {
  const ds = await ldb.get(MODEL_KEY);
  const model = ds
    ? JSON.parse(ds)
    : withRandomFirstBox(
        await loadStarterKit((localStorage.getItem(LANGUAGE) || 'nl') as Languages, thresholdColors)
      );
  const migrated = migrateModel(model);
  const legendsMigrated = migrateUsageLegends(model);
  const personasMigrated = migratePersonaImages(model);
  // Store a new user's starter collection at once, so its random first box stays put on reload.
  if (!ds || personasMigrated || migrated || legendsMigrated) await ldb.set(MODEL_KEY, JSON.stringify(model));
  const title = model.scenario?.label || '';
  setTitle(title);

  update({
    model: () => ({ ...model }),
    modelReady: true,
    title,
  });
};

const service: Service<State> = {
  onchange: (state) => state.model?.scenario?.label,
  run: (cell) => {
    const title = cell.state.model?.scenario?.label;
    setTitle(title);
    cell.update({ title });
  },
};

const app: MComp<State> = {
  services: [service],
  initial: {
    title: '',
    page: Dashboards.HOME,
    model: emptyModel(),
    modelReady: false,
    modelLoadError: false,
    fontSize:
      parseInt(localStorage.getItem(FONT_KEY) || '') || DEFAULT_FONT_SIZE,
    language: localStorage.getItem(LANGUAGE) || 'nl',
    mapHeight:
      parseInt(localStorage.getItem(MAP_HEIGHT_KEY) || '') ||
      DEFAULT_MAP_HEIGHT,
    boxTransposed: localStorage.getItem(BOX_TRANSPOSED_KEY) === 'true',
  },
};

export const cells = meiosisSetup<State>({ app });
initialize(cells().update).catch((error: unknown) => {
  console.error('Could not load collection', error);
  cells().update({ modelLoadError: true });
});

// initialize once on load
setFontSize(cells(), app.initial?.fontSize || DEFAULT_FONT_SIZE);

cells.map((state) => {
  if (state) {
    m.redraw();
  }
});
